import React from 'react';
import {
  footerLinksRowStyle,
  footerStackMobile,
  linkRowJustifyFromAlign,
  filterFooterLinks,
  normalizeFooterTemplate,
  resolveFooterStyles,
} from '../utils/footerBlockUtils';
import { FooterSocialIconsRow } from '../utils/footerSocialIcons';

export type FooterBlockProps = {
  content: Record<string, any>;
  styles: Record<string, any>;
  globalFontFamily?: string;
  isMobile?: boolean;
  isPalettePreview?: boolean;
};

const preventNav = (e: React.MouseEvent) => e.preventDefault();

function FooterLinks({
  links,
  color,
  justify,
  variant = 'default',
  extraStyle,
}: {
  links: Array<{ text: string; url?: string }>;
  color: string;
  justify: 'flex-start' | 'center' | 'flex-end';
  variant?: 'default' | 'minimal' | 'gradient' | 'onDark';
  extraStyle?: React.CSSProperties;
}) {
  if (!links?.length) return null;

  const linkStyle: React.CSSProperties =
    variant === 'minimal'
      ? { color, textDecoration: 'none', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase' }
      : variant === 'gradient'
        ? {
            color: '#FFFFFF',
            textDecoration: 'none',
            fontSize: '13px',
            opacity: 0.9,
            borderBottom: '1px solid rgba(255,255,255,0.3)',
            paddingBottom: 2,
          }
        : { color, textDecoration: 'none', fontSize: '13px', fontWeight: variant === 'onDark' ? 400 : 500 };

  return (
    <div style={{ ...footerLinksRowStyle(justify), ...extraStyle }}>
      {links.map((link, index) => (
        <a key={index} href={link.url || '#'} onClick={preventNav} style={linkStyle}>
          {link.text}
        </a>
      ))}
    </div>
  );
}

export const FooterBlock: React.FC<FooterBlockProps> = ({
  content,
  styles,
  globalFontFamily,
  isMobile = false,
  isPalettePreview = false,
}) => {
  const footerStyles = resolveFooterStyles(content, styles, globalFontFamily, isMobile, isPalettePreview);
  const stackMobile = footerStackMobile(isMobile, isPalettePreview);
  const template = normalizeFooterTemplate(content.template);
  const ta = String(footerStyles.textAlign || '');
  const linkJustify = linkRowJustifyFromAlign(ta);
  const fontFamily = (footerStyles.fontFamily as string) || globalFontFamily || "'Inter', sans-serif";
  const color = (footerStyles.color as string) || '#64748B';
  const padding = (footerStyles.padding as string) || '40px 24px';
  const fontSize = (footerStyles.fontSize as string) || '14px';
  const bg = (footerStyles.backgroundColor as string) || '#F8FAFC';
  const textAlign = (footerStyles.textAlign as React.CSSProperties['textAlign']) || 'center';

  const socialPlatforms = content.socialPlatforms || [];
  const socialIconColor = content.socialIconColor || '#FFFFFF';
  const socialBgColor = content.socialBgColor || '#111827';
  const iconStyle = content.socialIconStyle || 'filled';
  const links = filterFooterLinks(content.links);

  const socialRow = (opts: {
    boxSize?: number;
    iconSize?: number;
    bg?: string;
    iconColor?: string;
    radius?: string | number;
    align?: 'left' | 'center' | 'right';
  }) => (
    <FooterSocialIconsRow
      platforms={socialPlatforms}
      boxSize={opts.boxSize ?? 40}
      iconSize={opts.iconSize ?? 20}
      bgColor={opts.bg ?? socialBgColor}
      iconColor={opts.iconColor ?? socialIconColor}
      borderRadius={opts.radius ?? '10px'}
      iconStyle={iconStyle}
      align={
        opts.align ??
        (stackMobile ? 'center' : textAlign === 'right' ? 'right' : textAlign === 'left' ? 'left' : 'center')
      }
    />
  );

  if (template === 'complete') {
    const outerAlign = stackMobile ? 'center' : (ta === 'justify' ? 'center' : textAlign) || 'left';
    const socialAlign: 'left' | 'center' | 'right' = stackMobile
      ? 'center'
      : outerAlign === 'right'
        ? 'right'
        : outerAlign === 'left'
          ? 'left'
          : 'center';

    return (
      <div
        style={{
          padding,
          backgroundColor: bg || '#FFFFFF',
          fontSize,
          color,
          fontFamily,
          textAlign: outerAlign,
        }}
      >
        {content.companyName && (
          <h3 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: 700, color: '#111827' }}>
            {content.companyName}
          </h3>
        )}
        {content.location && (
          <p style={{ margin: '0 0 16px', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{content.location}</p>
        )}
        {content.disclaimer && (
          <p style={{ margin: '0 0 24px', fontSize: '13px', lineHeight: 1.5, color: '#6B7280' }}>
            {content.disclaimer}
          </p>
        )}
        {(content.appStoreUrl || content.googlePlayUrl) && (
          <>
            {content.appHeading && (
              <h4 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: 600, color: '#111827' }}>
                {content.appHeading}
              </h4>
            )}
            <div
              style={{
                display: 'flex',
                gap: 12,
                marginBottom: 24,
                flexWrap: 'wrap',
                justifyContent: stackMobile ? 'center' : linkJustify,
              }}
            >
              {content.appStoreUrl && (
                <a
                  href={content.appStoreUrl}
                  onClick={preventNav}
                  style={{
                    display: 'inline-block',
                    backgroundColor: '#000',
                    color: '#FFF',
                    padding: '10px 16px',
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    textDecoration: 'none',
                  }}
                >
                  Download on the App Store
                </a>
              )}
              {content.googlePlayUrl && (
                <a
                  href={content.googlePlayUrl}
                  onClick={preventNav}
                  style={{
                    display: 'inline-block',
                    backgroundColor: '#000',
                    color: '#FFF',
                    padding: '10px 16px',
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: 600,
                    textDecoration: 'none',
                  }}
                >
                  GET IT ON Google Play
                </a>
              )}
            </div>
          </>
        )}

        {socialPlatforms.length > 0 && (
          <div
            className="footer-complete-bottom"
            style={{
              display: 'flex',
              justifyContent: stackMobile ? 'center' : 'flex-end',
              marginTop: 24,
              paddingTop: 20,
              borderTop: '1px solid #E5E7EB',
            }}
          >
            <div className="footer-complete-social">{socialRow({ align: stackMobile ? 'center' : socialAlign })}</div>
          </div>
        )}
      </div>
    );
  }

  if (template === 'minimal') {
    return (
      <div
        style={{
          padding,
          backgroundColor: bg,
          textAlign,
          fontSize,
          color,
          fontFamily,
          borderTop: '1px solid #F1F5F9',
          letterSpacing: '0.02em',
        }}
      >
        {content.text && <p style={{ margin: '0 0 16px', lineHeight: 1.6 }}>{content.text}</p>}
        <FooterLinks links={links} color={color} justify={linkJustify} variant="minimal" />
      </div>
    );
  }

  if (template === 'gradient') {
    const gradientBg = String(bg).includes('gradient')
      ? bg
      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    return (
      <div
        style={{
          padding,
          background: gradientBg,
          textAlign,
          fontSize,
          color: '#FFFFFF',
          fontFamily,
        }}
      >
        {content.text && (
          <p style={{ margin: '0 0 20px', lineHeight: 1.6, opacity: 0.95 }}>{content.text}</p>
        )}
        <FooterLinks links={links} color="#FFF" justify={linkJustify} variant="gradient" />
        {socialPlatforms.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {socialRow({ bg: 'rgba(255,255,255,0.2)', iconColor: '#FFF', radius: '50%' })}
          </div>
        )}
      </div>
    );
  }

  if (template === 'social') {
    return (
      <div
        style={{
          padding,
          backgroundColor: bg || '#0F172A',
          textAlign: 'center',
          fontSize,
          color: color || '#E2E8F0',
          fontFamily,
        }}
      >
        {content.companyName && (
          <h3 style={{ margin: '0 0 12px', fontSize: 22, fontWeight: 700, color: '#FFFFFF' }}>
            {content.companyName}
          </h3>
        )}
        {content.text && (
          <p style={{ margin: '0 0 28px', lineHeight: 1.6, opacity: 0.85, fontSize: 15 }}>{content.text}</p>
        )}
        {socialPlatforms.length > 0 && (
          <div style={{ marginBottom: 28 }}>
            {socialRow({
              boxSize: 44,
              iconSize: 22,
              bg: '#FFFFFF',
              iconColor: '#0F172A',
              radius: '50%',
              align: 'center',
            })}
          </div>
        )}
        {links.length > 0 && (
          <div style={{ paddingTop: 24, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
            <FooterLinks links={links} color={color} justify="center" variant="onDark" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      style={{
        padding,
        backgroundColor: bg,
        textAlign,
        fontSize,
        color,
        fontFamily,
      }}
    >
      {content.text && <p style={{ margin: '0 0 24px', lineHeight: 1.6 }}>{content.text}</p>}
      <FooterLinks links={links} color={color} justify={linkJustify} />
    </div>
  );
};
